import React, { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import { faFlagCheckered } from '@fortawesome/free-solid-svg-icons';
import { Tabs } from '../../../components/Tabs';

import { CalendarValue } from '../../../components/DateTimePicker';
import { useAuth } from '../../../context/Auth';
import { dispatchService } from '../../../services/central-api/dispatch';
import moment from 'moment';
import Toast from 'react-native-toast-message';
import UnitFormDetails from './UnitFormDetails';
import UnitFormAgents from './UnitFormAgents';
import UnitFormHistoric from './UnitFormHistoric';
import UnitFormOccurrences from './UnitFormOccurrences';
import FinishTeamModal from './FinishTeamModal';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { EquipmentAlreadyInUse } from '../../../services/central-api/deac';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import Badge, { BadgeColor } from '../../../components/Badge';
import { useForm } from 'react-hook-form';
import { DispatchParamList } from '../../../typings/Params';
import { useStyles } from 'react-native-unistyles';
import DropdownButton from '../../../components/DropdownButton';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { dispatchModule } from '../../../routes/dispatch';
import { useCurrentApp } from '../../../context/CurrentApp';
import { WindowInformation } from '../../../services/window-information';
import UnitFormPolicePatrolSchedule from './UnitFormPolicePatrolSchedule';
import ExportRasRdpPDFDetails from './ExportRasRdpPDFDetails';
import DrawRouteMap from '../../../components/DrawRouteMap';
import MyDataTable from '../../../components/MyDataTable';
import Divider from '../../../components/Divider';
import { InfoInRow } from '../../../components/InfoInRow';
import { InvalidValue } from '../../../services/central-api/base-service';

export interface UnitValuesForm {
    unitNameId: number;
    unitName?: { id: number; name: string; };
    unitProgramId: number;
    description: string;
    actingBodyId: number;
    actingBodyUnitId: number;
    actingBodyUnit?: {
        id: number;
        name: string;
        actingBody?: {
            id: number;
            name: string;
        } | null;
    };
    actingBodyCommandId: number;
    actingBodyCommand?: { id: number; name: string; };
    estimatedStart: {
        date: CalendarValue;
        hour: string;
    };
    estimatedFinish: {
        date: CalendarValue;
        hour: string;
    };
    definitiveStart?: string;
    definitiveFinish?: string;
    paused: boolean;
    displaced: boolean;
    equipments: number[];
    driverId: number;
    personInChargeId: number;
    agents: number[];
    createdAt?: string;
    hasPlate: boolean;
}

export default function UnitForm() {
    const route = useRoute<RouteProp<DispatchParamList, 'UnitForm'>>();
    const unitId = route.params?.id;
    const { theme } = useStyles();
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<DispatchParamList>>();
    const currentApp = useCurrentApp();
    const windowInfo = WindowInformation();

    const defaultEmptyValues: UnitValuesForm = {
        unitNameId: 0,
        unitName: undefined,
        unitProgramId: 0,
        description: '',
        actingBodyId: 0,
        actingBodyUnitId: 0,
        actingBodyUnit: undefined,
        actingBodyCommandId: 0,
        actingBodyCommand: undefined,
        estimatedStart: { date: null, hour: '' },
        estimatedFinish: { date: null, hour: '' },
        definitiveStart: undefined,
        definitiveFinish: undefined,
        paused: false,
        displaced: false,
        equipments: [],
        driverId: 0,
        personInChargeId: 0,
        agents: [],
        createdAt: undefined,
        hasPlate: false,
    };

    const form = useForm<UnitValuesForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange',
        shouldUnregister: false
    });


    useEffect(() => {
        form.setValue('actingBodyId', Number(route.params?.actingBodyId) || 0);
        form.setValue('actingBodyUnitId', Number(route.params?.actingBodyUnitId) || 0);
        form.setValue('actingBodyCommandId', Number(route.params?.actingBodyCommandId) || 0);
        form.setValue('unitNameId', Number(route.params?.unitNameId) || 0);
    }, [route.params]);

    const [unit, setUnit] = useState<Unit>();
    const [unitGeolocationRoute, setUnitGeolocationRoute] = useState<{ latitude: number; longitude: number; timestamp: number; selected?: boolean; }[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [modifiedAgentAssignments, setModifiedAgentAssignments] = useState<{ [agentId: number]: number; }>({});

    const [earlyFinishModal, setEarlyFinishModal] = useState(false);

    const isFocused = useIsFocused();
    useEffect(() => {
        if (!authenticatedUser) {
            return;
        }

        if (isFocused) {
            loadPageInfo();
        }
    }, [route.params, isFocused, authenticatedUser]);

    async function loadPageInfo() {
        try {
            setIsLoading(true);
            if (unitId) {
                const unit = await dispatchService.getUnit(unitId);
                console.log(unit);
                const driver = unit.UnitUsers?.find(uu => uu.UnitUser.isDriver);
                const personInCharge = unit.UnitUsers?.find(uu => uu.UnitUser.isPersonInCharge);
                form.reset({
                    ...unit,
                    estimatedStart: {
                        date: new Date(unit.estimatedStart),
                        hour: moment(unit.estimatedStart).format('HH:mm')
                    },
                    estimatedFinish: {
                        date: new Date(unit.estimatedFinish),
                        hour: moment(unit.estimatedFinish).format('HH:mm')
                    },
                    actingBodyId: unit.actingBodyUnit?.actingBodyId,
                    equipments: unit.equipments?.map(e => e.id) || [],
                    driverId: driver ? driver.id : 0,
                    personInChargeId: personInCharge ? personInCharge.id : 0,
                    agents: unit.UnitUsers?.map(uu => uu.id) || [],
                    hasPlate: unit.unitName.unitCategory.hasPlate
                });
                setUnit(unit);

                if (authenticatedUser?.permissions.dispatch_watch_unit_geolocation) {
                    const unitGeolocationRoute = await dispatchService.getUnitGeolocationRoute(unitId);
                    setUnitGeolocationRoute(unitGeolocationRoute.map(u => ({ ...u, timestamp: Number(u.rowKey) })));
                }
            } else {
                let actingBodyId = 0;
                let actingBodyUnitId = 0;
                if (!(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_units) && authenticatedUser?.role?.actingBody?.id) {
                    actingBodyId = authenticatedUser.role.actingBody.id;
                }

                if (!(authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_acting_body_units) && authenticatedUser?.actingBodyUnitId) {
                    actingBodyId = authenticatedUser.actingBodyUnit.actingBodyId;
                    actingBodyUnitId = authenticatedUser.actingBodyUnitId;
                }

                form.reset({
                    ...defaultEmptyValues,
                    actingBodyId: actingBodyId,
                    actingBodyUnitId: actingBodyUnitId,
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    async function save() {
        const data = form.getValues();
        const start = moment(`${moment(data.estimatedStart.date).format('DD/MM/YYYY')} ${data.estimatedStart.hour}`, 'DD/MM/YYYY HH:mm').toLocaleString();
        const finish = moment(`${moment(data.estimatedFinish.date).format('DD/MM/YYYY')} ${data.estimatedFinish.hour}`, 'DD/MM/YYYY HH:mm').toLocaleString();

        try {
            if (unitId) {
                await dispatchService.updateUnit({
                    ...data,
                    unitId: unitId,
                    driverId: data.driverId != 0 ? data.driverId : undefined,
                    personInChargeId: data.personInChargeId != 0 ? data.personInChargeId : undefined,
                    usersToSet: data.agents,
                    equipmentsToSet: data.equipments,
                    modifiedAgentAssignments,
                    estimatedStart: start,
                    estimatedFinish: finish
                });
            } else {
                await dispatchService.createUnit({
                    ...data,
                    driverId: data.driverId != 0 ? data.driverId : undefined,
                    personInChargeId: data.personInChargeId != 0 ? data.personInChargeId : undefined,
                    usersToSet: data.agents,
                    equipmentsToSet: data.equipments,
                    modifiedAgentAssignments,
                    estimatedStart: start,
                    estimatedFinish: finish
                });
            }
            form.reset(defaultEmptyValues);
            navigation.navigate('Units', { limit: 25, page: 0 });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof EquipmentAlreadyInUse) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('equipmentAlreadyInUse'),
                });
            } else if (err instanceof InvalidValue) {
                if (err.field === 'driverId') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('driverIdRequired'),
                    });
                } else if (err.field === 'personInChargeId') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('personInChargeIdRequired'),
                    });
                }
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function isFormValid(): boolean {
        const data = form.getValues();
        if (!form.formState.isValid) {
            return false;
        }

        if (data.estimatedStart.date == null || data.estimatedFinish.date == null) {
            return false;
        }

        const start = moment(`${moment(data.estimatedStart.date).format('DD/MM/YYYY')} ${data.estimatedStart.hour}`, 'DD/MM/YYYY HH:mm');
        const finish = moment(`${moment(data.estimatedFinish.date).format('DD/MM/YYYY')} ${data.estimatedFinish.hour}`, 'DD/MM/YYYY HH:mm');

        if (!unitId && start.format('YYYY-MM-DD HH:mm') < moment().subtract(1, 'minute').format('YYYY-MM-DD HH:mm')) {
            return false;
        }
        return start.valueOf() <= finish.valueOf() && moment.duration(finish.diff(start)).asHours() >= 1;
    }

    function renderPointContent(point: { latitude: number; longitude: number; }) {
        return <View style={{ paddingVertical: 10, paddingLeft: 50, flexDirection: 'column', rowGap: 5 }}>
            <InfoInRow title={'latitude'} value={point.latitude} />
            <InfoInRow title={'longitude'} value={point.longitude} />
        </View>;
    }

    function renderStatus(unit?: Unit) {

        if (!unit) {
            return;
        }

        let customStyle: BadgeColor = 'green';
        let customName = 'available';

        if (form.watch('definitiveStart') && form.watch('definitiveFinish')) {
            customStyle = 'gray';
            customName = 'finished_f';
        } else if (form.watch('paused')) {
            customStyle = 'purple';
            customName = 'femalePaused';
        } else if (form.watch('displaced')) {
            customStyle = 'blue';
            customName = 'displace';
        } else if (!form.watch('definitiveStart') && !form.watch('definitiveFinish') && moment(form.watch('estimatedStart.date')).toDate().getTime() > moment().toDate().getTime()) {
            customStyle = 'yellow';
            customName = 'waiting';
        } else if (!form.watch('definitiveStart') && !form.watch('definitiveFinish') && moment(form.watch('estimatedStart.date')).toDate().getTime() < moment().toDate().getTime()) {
            customStyle = 'orange';
            customName = 'overdue';
        } else if (form.watch('definitiveStart') && !form.watch('definitiveFinish') && moment(form.watch('estimatedFinish.date')).toDate().getTime() < moment().toDate().getTime()) {
            customStyle = 'red';
            customName = 'femaleExceeded';
        }

        return <Badge mode='SQUARE' color={customStyle}>{translate(customName)}</Badge>;
    }

    function unitHasRasRdpReport() {
        if (unit && unit.signableReports && unit.signableReports.length > 0) {
            const rasRdpReport = unit.signableReports.find(r => r.reportType === 'rdp_ras');

            if (rasRdpReport) {
                return true;
            }
        }

        return false;
    }

    function getRasRdpReport() {
        if (unit && unit.signableReports && unit.signableReports.length > 0) {
            const rasRdpReport = unit.signableReports.find(r => r.reportType === 'rdp_ras');

            return rasRdpReport;
        }

        return undefined;
    }

    return <Container>
        <Header
            title={translate('registerTeam')}
            actions={!form.watch('definitiveFinish') ?
                <View style={{ justifyContent: 'flex-end', flexDirection: 'row', gap: 10 }}>
                    {route.params?.id &&
                        <>
                            <View style={{ paddingRight: 8 }}>
                                <DropdownButton
                                    isButton={true}
                                    offsetX={!windowInfo.isMobile ? -120 : 0}
                                    offsetY={41}
                                    size={200}
                                    buttonType='tertiary'
                                    buttonIcon={faChevronDown as IconDefinition}
                                    buttonLabel='actions'
                                    actions={[{
                                        content: translate('goToTeamName'),
                                        onPress: async () => {
                                            if (unit?.unitNameId) {
                                                navigation.navigate('TeamName', { id: unit.unitNameId });
                                            }
                                        },
                                        omit: !dispatchModule.screens.TeamName.canAccess(authenticatedUser, currentApp),
                                    }]}
                                />
                            </View>
                            <CustomButton
                                text={translate('finish')}
                                onPress={() => {
                                    setEarlyFinishModal(true);
                                }}
                                disabled={!isFormValid()}
                                type='secondary'
                                icon={faFlagCheckered}
                            />
                        </>
                    }
                    <CustomButton text={translate('save')} onPress={save} disabled={!isFormValid()} />
                </View>
                : unitHasRasRdpReport() && <ExportRasRdpPDFDetails report={getRasRdpReport()} />
            }
            goBackAction={() => {
                form.reset(defaultEmptyValues);
                navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Units', { limit: 25, page: 0 });
            }}
        />
        <Card>
            {isLoading ? <ActivityIndicator size='small' color={theme.colors.loadingColor} /> : <Tabs
                endContent={
                    <View style={[{ justifyContent: 'center', alignSelf: 'flex-end' }]}>
                        {renderStatus(unit)}
                    </View>
                }
                tabs={[{
                    key: 'details',
                    label: translate('details')
                }, {
                    key: 'agents',
                    label: translate('agents')
                }, {
                    key: 'occurrences',
                    label: translate('occurrences'),
                    omit: unitId === undefined,
                }, {
                    key: 'historic',
                    label: translate('historic'),
                    omit: unitId === undefined,
                }, {
                    key: 'policePatrolSchedule',
                    label: translate('policePatrolSchedule'),
                    omit: unitId === undefined,
                }, {
                    key: 'geolocationRoute',
                    label: translate('geolocationRoute'),
                    omit: unitId === undefined || unitGeolocationRoute.length === 0,
                }]}>
                {({ selectedTab }) => {
                    if (selectedTab === 'details') {
                        return <ScrollView style={{ paddingRight: 10 }}>
                            <UnitFormDetails
                                form={form}
                                unitId={unitId}
                            />
                        </ScrollView>;
                    } else if (selectedTab === 'agents') {
                        return <UnitFormAgents
                            unit={unit}
                            setUnit={setUnit}
                            form={form}
                            actingBodyId={form.watch('actingBodyId')}
                            modifiedAgentAssignments={modifiedAgentAssignments}
                            setModifiedAgentAssignments={setModifiedAgentAssignments}
                            isFinished={!!form.watch('definitiveFinish')}
                        />;
                    } else if (unit && selectedTab === 'occurrences') {
                        return <UnitFormOccurrences occurrences={unit.OccurrenceUnits} />;
                    } else if (unitId && selectedTab === 'historic') {
                        return <UnitFormHistoric unitId={unitId} />;
                    } else if (unitId && selectedTab === 'policePatrolSchedule') {
                        return <UnitFormPolicePatrolSchedule unitId={unitId} />;
                    } else if (unitId && selectedTab === 'geolocationRoute') {

                        return <View style={{ flex: 1, flexDirection: 'row' }}>
                            <View style={{ maxWidth: 250, width: '100%', height: '100%', flex: 1 }}>
                                <MyDataTable
                                    data={unitGeolocationRoute}
                                    pointerOnHover
                                    expandableRowsComponent={({ data }) => renderPointContent(data)}
                                    expandableRows
                                    onRowClicked={(row) => {
                                        if (row.selected) {
                                            row.selected = false;
                                        } else {
                                            for (const point of unitGeolocationRoute) {
                                                point.selected = false;
                                            }
                                            row.selected = true;
                                        }
                                        setUnitGeolocationRoute([...unitGeolocationRoute]);
                                    }}
                                    columns={[{
                                        name: translate('time'),
                                        selector: row => moment(row.timestamp).format('DD/MM/YYYY - HH:mm:ss'),

                                    }]}
                                />
                            </View>
                            {!windowInfo.isMobile && <Divider orientation='vertical' />}
                            <View style={{ width: '100%', height: '100%', flex: 1 }}>
                                {unitGeolocationRoute.length > 0 && <DrawRouteMap
                                    cameraPositions={unitGeolocationRoute}
                                />
                                }
                            </View>
                        </View>;
                    }
                }}
            </Tabs>}
        </Card>
        {unitId && <FinishTeamModal
            isModalVisible={earlyFinishModal}
            setModalVisible={setEarlyFinishModal}
            unit={{ id: unitId }}
            onClose={(success: boolean) => {
                if (success) {
                    form.reset(defaultEmptyValues);
                    navigation.navigate('Units', { limit: 25, page: 0 });
                }
            }}
        />}
    </Container>;
}
